(function () {
  if (window.productItemVideoReveal) {
    window.productItemVideoReveal.init();
    return;
  }

  let observer;
  let removalObserver;
  const pendingVideoLoads = new WeakMap();
  const pendingNativeVideos = new WeakSet();
  const nativeVideos = new Map();
  const hoverGalleryQuery = "(min-width: 1024px) and (pointer: fine)";
  const hoverGalleryMedia = window.matchMedia?.(hoverGalleryQuery);

  function videoIsActive(gallery) {
    return !gallery.hasAttribute("data-product-item-video-hover-gallery") || !canUseHoverGallery() ||
      (!gallery.matches(":hover, :focus-within") && !gallery.classList.contains("product-item__media-gallery--hovering"));
  }

  function syncGalleryVideo(gallery) {
    if (gallery.dataset.productItemVideoHoverGallery === undefined) return;
    const video = gallery.querySelector("video[data-product-item-video]");
    nativeVideos.get(video)?.controller.setActive(videoIsActive(gallery));
  }

  function watchNativeVideos() {
    if (removalObserver) return;
    removalObserver = new MutationObserver((records) => {
      if (records.some((record) => record.removedNodes.length)) {
        nativeVideos.forEach((record, video) => {
          if (video.isConnected) return;
          record.abort.abort();
          record.controller.destroy();
          nativeVideos.delete(video);
        });
      }
      // 保留 init(root) 契约，并覆盖同一节点重连及 Section Rendering 后的原生视频。
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches("video[data-product-item-video]")) bindNativeVideo(node);
        node.querySelectorAll("video[data-product-item-video]").forEach(bindNativeVideo);
      }));
    });
    removalObserver.observe(document.body, { childList: true, subtree: true });
    hoverGalleryMedia?.addEventListener("change", () => {
      nativeVideos.forEach((record, video) => record.controller.setActive(videoIsActive(video.parentElement)));
    });
  }

  function bindNativeVideo(video) {
    if (nativeVideos.has(video) || pendingNativeVideos.has(video) || !video.dataset.videoController) return;
    watchNativeVideos();
    pendingNativeVideos.add(video);
    import(/* @vite-ignore */ video.dataset.videoController).then(({ ThemeVideoController, videoSources }) => {
      if (!video.isConnected || nativeVideos.has(video)) return;
      const gallery = video.parentElement;
      const sources = videoSources(video.dataset.video, video.dataset.src);
      const abort = new AbortController();
      const controller = new ThemeVideoController(video, {
        container: gallery,
        sources: () => ({ key: video.dataset.src, sources }),
        autoplay: true,
        active: videoIsActive(gallery),
        threshold: 0.25,
        loadDelay: 250,
        maxDpr: 2,
        poster: gallery.querySelector("img.product-item__video-poster:not(.ds-responsive-image__placeholder)"),
        fallbackPoster: video.dataset.poster,
        onState: ({ frameReady }) => {
          video.classList.toggle("product-item__video-media--loaded", frameReady);
          video.dataset.productItemVideoLoaded = String(frameReady);
        },
      });
      nativeVideos.set(video, { controller, abort });
      ["pointerenter", "pointerleave", "focusin", "focusout"].forEach((name) => {
        gallery.addEventListener(name, () => queueMicrotask(() => {
          if (!abort.signal.aborted) syncGalleryVideo(gallery);
        }), { signal: abort.signal });
      });
    }).catch(() => { video.dataset.videoLoadError = "true"; })
      .finally(() => pendingNativeVideos.delete(video));
  }

  // 外部 iframe 保留原加载路径，不接入原生 video 的播放 API。
  function reveal(media) {
    media.classList.add("product-item__video-media--loaded");
    media.dataset.productItemVideoLoaded = "true";
  }

  function bind(media) {
    if (media.tagName === "VIDEO") { bindNativeVideo(media); return; }
    if (media.dataset.productItemVideoBound === "true") return;
    media.dataset.productItemVideoBound = "true";
    media.addEventListener("load", () => reveal(media), { once: true });
    window.setTimeout(() => {
      if (media.dataset.productItemVideoLoaded !== "true") reveal(media);
    }, 1600);
  }

  function loadVideoSource(media) {
    if (media.dataset.productItemSourceLoaded === "true" || !media.dataset.src) return;
    media.src = media.dataset.src;
    media.removeAttribute("data-src");
    media.dataset.productItemSourceLoaded = "true";
  }

  function observeVideo(media) {
    if (media.tagName === "VIDEO" || media.dataset.productItemObserved === "true") return;
    media.dataset.productItemObserved = "true";
    if (!("IntersectionObserver" in window)) { loadVideoSource(media); return; }
    if (!observer) observer = new IntersectionObserver((entries, activeObserver) => {
      entries.forEach((entry) => {
        window.clearTimeout(pendingVideoLoads.get(entry.target));
        if (!entry.isIntersecting) return;
        const timer = window.setTimeout(() => {
          if (!entry.target.isConnected) return;
          loadVideoSource(entry.target);
          pendingVideoLoads.delete(entry.target);
          activeObserver.unobserve(entry.target);
        }, 250);
        pendingVideoLoads.set(entry.target, timer);
      });
    }, { rootMargin: "0px", threshold: 0.25 });
    observer.observe(media);
  }

  function getHoverImages(gallery) {
    return Array.from(gallery.children).filter((child) =>
      child.classList.contains("product-item__hover-image"),
    );
  }

  function hydrateHoverImages(gallery) {
    gallery
      .querySelectorAll(":scope > template[data-product-item-hover-template]")
      .forEach((template) => {
        template.replaceWith(template.content.cloneNode(true));
      });
  }

  function getHoverIndicators(gallery) {
    return Array.from(
      gallery.querySelectorAll(".product-item__hover-indicator-dot"),
    );
  }

  function canUseHoverGallery() {
    return Boolean(hoverGalleryMedia && hoverGalleryMedia.matches);
  }

  function bindHoverGallery(gallery) {
    if (gallery.dataset.productItemHoverGalleryBound === "true") return;

    const hoverImageCount = Number(gallery.dataset.productItemHoverCount || 0);
    if (hoverImageCount < 2) return;
    let hoverBounds = null;
    let hoverImages = getHoverImages(gallery);
    const hoverIndicators = getHoverIndicators(gallery);
    let hoverImagesHydrated = false;
    let activeIndex = 0;

    function setActiveHoverImage(imageIndex) {
      if (hoverImages.length === 0) return;
      const nextIndex = Math.min(Math.max(imageIndex, 1), hoverImages.length);
      if (nextIndex === activeIndex) return;

      hoverImages.forEach((image, index) => {
        image.classList.toggle(
          "product-item__hover-image--active",
          index === nextIndex - 1,
        );
      });
      hoverIndicators.forEach((indicator, index) => {
        indicator.classList.toggle(
          "product-item__hover-indicator-dot--active",
          index === nextIndex - 1,
        );
      });
      activeIndex = nextIndex;
    }

    function startHover() {
      if (hoverBounds) return;
      // Read layout before mounting deferred images or changing hover classes.
      hoverBounds = gallery.getBoundingClientRect();
      if (!hoverImagesHydrated) {
        hydrateHoverImages(gallery);
        // Cache live images after the deferred templates have been mounted.
        hoverImages = getHoverImages(gallery);
        hoverImagesHydrated = true;
        activeIndex = 0;
      }
      setActiveHoverImage(1);
      gallery.classList.add("product-item__media-gallery--hovering");
      syncGalleryVideo(gallery);
    }

    gallery.dataset.productItemHoverGalleryBound = "true";
    setActiveHoverImage(1);

    gallery.addEventListener("pointerenter", () => {
      if (canUseHoverGallery()) startHover();
    });

    gallery.addEventListener("pointermove", (event) => {
      if (!canUseHoverGallery()) return;

      startHover();
      const rect = hoverBounds;
      if (rect.width === 0) return;

      const offsetX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
      const zoneIndex = Math.min(Math.floor((offsetX / rect.width) * 3) + 1, 3);
      setActiveHoverImage(zoneIndex);
    });

    gallery.addEventListener("pointerleave", () => {
      hoverBounds = null;
      gallery.classList.remove("product-item__media-gallery--hovering");
      syncGalleryVideo(gallery);
      setActiveHoverImage(1);
    });
  }

  function init(rootElement) {
    const scope = rootElement || document;
    if (scope.matches?.("[data-product-item-video]")) {
      bind(scope);
      observeVideo(scope);
    }

    if (scope.matches?.("[data-product-item-hover-gallery]")) {
      bindHoverGallery(scope);
    }

    scope.querySelectorAll?.("[data-product-item-video]").forEach((media) => {
      bind(media);
      observeVideo(media);
    });

    scope
      .querySelectorAll?.("[data-product-item-hover-gallery]")
      .forEach((gallery) => {
        bindHoverGallery(gallery);
      });
  }

  window.productItemVideoReveal = {
    init,
  };

  init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
